import type { RootState } from '@appStore/store'
import type { Dayjs } from 'dayjs'

import { createSelector } from '@reduxjs/toolkit'
import { appDay } from 'shared/day'

import type { Message, SeenResponse } from '../model/types'

import { api } from '../api'
import { createMessageMap } from './utils'

const getRoot = (state: RootState) => state
const placeholderId = (id: string) => id

const selectSeen = createSelector(placeholderId, id =>
	api.endpoints.seen.select(id)
)

const selectChatSeen = (state: RootState, conversation: string) =>
	selectSeen(conversation)(state)

const selectLastSeen = createSelector(
	selectChatSeen,
	seen =>
		seen.data?.reduce<Dayjs | null>((date, item) => {
			const seenAt = appDay(item.seen_at)

			if (date === null) {
				return seenAt
			}

			return date.isBefore(seenAt) ? seenAt : date
		}, null) ?? null
)

const selectUnreadMap = createSelector(
	selectChatSeen,
	seen =>
		seen.data?.reduce<Record<string, Omit<SeenResponse, 'message_id'>>>(
			(map, stats) => {
				const { participant, seen_at } = stats

				map[stats.message_id] = {
					participant,
					seen_at
				}

				return map
			},
			{}
		) ?? {}
)

const createMessagesSelector = createSelector(placeholderId, id =>
	api.endpoints.messages.select(id)
)

const selectConversation = createSelector(
	getRoot,
	(_: RootState, id: string) => createMessagesSelector(id),
	(root, select) => {
		const messages: Message[] = []
		let prevCursor = ''

		const { data, isUninitialized, originalArgs: id } = select(root)
		if (!isUninitialized && data) {
			messages.push(...data.items)

			prevCursor = data.prevCursor ?? ''
			if (prevCursor) {
				function checkPrev() {
					const select = api.endpoints.messagesPrev.select({
						id: id!,
						prevCursor
					})

					const { data, isUninitialized } = select(root)

					if (!isUninitialized && data) {
						messages.push(...data.items)
						prevCursor = data.prevCursor ?? ''

						if (prevCursor) {
							checkPrev()
						}
					}
				}

				checkPrev()
			}
		}

		return createMessageMap(messages)
	}
)

export const selectors = {
	conversation: selectConversation,
	lastSeen: selectLastSeen,
	unreadMap: selectUnreadMap
}
